import type { CatalogFacet, CatalogSource, CatalogStation } from '../domain/catalog';
import type { HistoryEntry, ListenStat } from '../domain/history';
import type { Track } from '../domain/icy-parse';
import { findByUrl } from '../domain/library';
import type { LibraryLoadFailure } from '../domain/library-repository';
import { DEFAULT_SETTINGS, type Settings } from '../domain/settings';
import type { SleepState } from '../domain/sleep';
import type { Station } from '../domain/stations';
import type { StreamVia } from '../domain/stream-url';
import { type Lang, readStoredLang } from '../i18n';

/**
 * Začetna tema iz `data-theme`, ki ga anti-FOUC skript postavi pred modulom.
 * Ograjeno s `try`: enotni testi tečejo v Node brez `document`.
 */
function readInitialTheme(): 'dark' | 'light' {
  try {
    return document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark';
  } catch {
    return 'dark';
  }
}

import type { AudioErrorKind, PlaybackState } from '../ports/audio';
import type { CatalogFailure } from '../ports/catalog';
import { createStore } from './index';

export const TABS = ['stations', 'search', 'history', 'timer', 'settings'] as const;
export type TabId = (typeof TABS)[number];

/**
 * Pogled predvajalnika (P2 §3.11, §7).
 *
 * Tip stoji v store in ne v komponenti, ker je stanje aplikacije, ne videz —
 * obratna smer bi pomenila, da store uvaža iz `ui/`, torej sloj navzgor (T1 §2).
 */
export const PLAYER_MODES = ['bar', 'full', 'car'] as const;
export type PlayerMode = (typeof PLAYER_MODES)[number];

export interface AppState {
  tab: TabId;
  lang: Lang;
  /**
   * Dejavna tema. Vir resnice je `data-theme` na korenu, ki ga postavita
   * anti-FOUC skript in `ds/js/theme.js`; store ga zrcali prek opazovalca v
   * `main.ts`, da zaslon Nastavitve sledi tudi preklopu iz glave (T2 §2).
   */
  theme: 'dark' | 'light';

  /** Vse postaje. Vsaka sprememba je NOVO polje — store primerja po referenci. */
  stations: readonly Station[];
  settings: Settings;
  /**
   * Iskanje po lastnem seznamu (P2 §1.6). Ni v nastavitvah namerno: iskalni
   * niz je delo trenutka in bi ob naslednjem zagonu skril postaje brez
   * pojasnila, zakaj jih ni.
   */
  query: string;
  /** Dokler shramba ne odgovori, seznam ni prazen — le še ni znan. */
  libraryReady: boolean;
  /** Migracija je odpovedala; vmesnik mora ponuditi izvoz surovih podatkov. */
  libraryFailure: LibraryLoadFailure | null;

  /** Izbrana postaja; `null`, dokler uporabnik ne izbere. */
  stationId: string | null;
  playback: PlaybackState;
  /** Zaporedna številka ponovnega poskusa; 0, kadar poskusa ni. */
  attempt: number;
  route: StreamVia | null;
  /** Vzrok zadnje ustavitve; določa, katero sporočilo je resnično (P4 §5). */
  error: AudioErrorKind | null;

  /**
   * Skladba, ki jo postaja oddaja zdaj.
   *
   * `null` je **pogosto in legitimno** stanje, ne napaka: govorne postaje in
   * marsikateri Icecast naslova ne pošljejo (T3 §3). Vrstica takrat pokaže
   * ime postaje in nič ne manjka.
   */
  track: Track | null;
  /**
   * Ovitek za `track`.
   *
   * Nadomestek je **pogosto stanje, ne robni primer** (P2 §3.1): niti en vir
   * ne pozna vsake skladbe, govorne postaje pa naslova sploh ne pošljejo.
   */
  artworkUrl: string | null;

  /** Iskanje po spletni bazi (faza 4, P2 §2). */
  search: SearchState;

  /**
   * Zgodovina odigranih skladb (P2 §4), najnovejša prva.
   *
   * Le kotalno okno iz IndexedDB, ne cel nabor: seznam v pomnilniku je za
   * prikaz, resnica živi v shrambi (T2 §3). Zapolni se ob zagonu in raste ob
   * vsaki novi skladbi.
   */
  history: readonly HistoryEntry[];
  /** Agregiran čas poslušanja po postaji (P2 §4.5). */
  stats: readonly ListenStat[];
  /**
   * Časovnik spanja (P2 §5); `null`, kadar ni nastavljen.
   *
   * Živi tudi v `sessionStorage` — **ne sme preživeti zaprtja zavihka**
   * (T2 §3). Tik posodobitve teče v `store/sleep.ts`.
   */
  sleep: SleepState | null;

  /**
   * Kateri pogled predvajalnika je na vrsti (P2 §3.11, §7).
   *
   * `bar` je pripeta medijska vrstica, `full` razširjen pogled, `car` način za
   * avto. **Se ne shranjuje**: naslednji zagon se ne sme odpreti čez cel
   * zaslon, ker uporabnik takrat ni prosil za predvajalnik, ampak za
   * aplikacijo. Ni tudi v poti (`hash`) — pot nosi zavihek, ta pa je plast
   * nad njim (glej `main.ts`, gumb nazaj gre skozi `history.pushState`).
   */
  player: PlayerMode;
}

/**
 * Stanje zaslona Iskanje.
 *
 * Ločeno od `query`, ki išče po **lastnem** seznamu (P2 §1.6): to sta dve
 * iskanji z različnima viroma in različnima praznima stanjema, in njuno
 * zlitje bi pomenilo, da tipkanje na enem zaslonu spreminja drugega.
 */
export interface SearchState {
  query: string;
  /** ISO 3166-1 alpha-2, velike črke. `null` = brez filtra. */
  countryCode: string | null;
  tag: string | null;

  /**
   * 🔴 **Štiri stanja, ne dve** — nauk kaskade metapodatkov (T3 §3).
   *
   * `idle` je prvi obisk, ko se še ni iskalo; `ready` s praznim seznamom je
   * »ni zadetkov«. To nista ista stvar in pot ven je različna (P4 §6). Zliti
   * v en prazen seznam bi pomenilo, da uporabnik ob prvem odprtju zaslona
   * prebere, da njegove postaje ni.
   */
  status: 'idle' | 'loading' | 'ready' | 'failed';
  /** Vzrok neuspeha; določa, katero sporočilo je resnično (P4 §5). */
  failure: CatalogFailure | null;

  results: readonly CatalogStation[];
  /** Kateri vir je odgovoril. `webradiodb` pomeni degradiran način (T3 §1). */
  source: CatalogSource | null;
  /** Vir je vrnil manj od zahtevanega: gumba »pokaži več« ni. */
  exhausted: boolean;

  countries: readonly CatalogFacet[];
  tags: readonly CatalogFacet[];

  /**
   * Zadetek, ki se predposluša in **ni** v uporabnikovem seznamu (P2 §2.4).
   *
   * Živi tu in ne v `stations`: seznam je uporabnikov in vanj ne sme priti
   * nič, česar ni dodal sam — shramba, izvoz in ⏮ ⏭ ga vidijo vsi.
   * Predvajanje ga vidi prek `playableStations()`.
   */
  preview: Station | null;
}

export const EMPTY_SEARCH: SearchState = {
  query: '',
  countryCode: null,
  tag: null,
  status: 'idle',
  failure: null,
  results: [],
  source: null,
  exhausted: true,
  countries: [],
  tags: [],
  preview: null,
};

/**
 * Vse, kar se sme predvajati: uporabnikov seznam **in** zadetek v predposluhu.
 *
 * ⚠️ Vrne novo polje samo takrat, kadar predposluh obstaja. Sicer bi vsak
 * klic dal novo referenco in vsak izris bi izgledal kot sprememba seznama —
 * zaslon Postaje vrstice prezida natanko ob spremembi `id`-jev (glej
 * `ui/screens/stations.ts`), a ceno bi plačala vsaka druga primerjava.
 */
export function playableStations(state: AppState): readonly Station[] {
  const preview = state.search.preview;
  return preview === null ? state.stations : [...state.stations, preview];
}

/** Ali `id` stoji v uporabnikovem seznamu (in ne le v predposluhu). */
export function isListedStation(state: AppState, id: string | null): boolean {
  return id !== null && state.stations.some((s) => s.id === id);
}

/**
 * Postaja, pod katero se sme beležiti zgodovina in čas — vedno `id` iz
 * **uporabnikovega seznama**, nikoli id predposluha. `null` pomeni »ne beleži«.
 *
 * 🔴 Merilo je **članstvo v seznamu**, ne »ni predposluh«. Predposluh je begljiv:
 * ko se `search.preview` zamenja ali počisti, prejšnji id za en izris ni več
 * preview, predvajanje pa še teče — in zapis je padel v zgodovino pod id, ki ga
 * v `stations` ni. Zaslon Zgodovina je tak vnos pokazal kot »Removed station«,
 * vsak predposluh svojo skupino.
 *
 * Edina izjema je predposluh, ki ga uporabnik **med predvajanjem doda** v
 * seznam: postaja je ista, id pa nov — `addStation()` skuje svojega in
 * predvajanje teče naprej pod starim. Zato se tak primer prepozna po naslovu in
 * beleži pod id iz seznama; sicer bi zgodovina visela na id-ju, ki ob naslednjem
 * zagonu izgine.
 */
export function journaledStationId(state: AppState): string | null {
  if (state.playback !== 'playing' || state.stationId === null) return null;
  if (isListedStation(state, state.stationId)) return state.stationId;

  const preview = state.search.preview;
  if (preview === null || preview.id !== state.stationId) return null;
  return findByUrl(state.stations, preview.url)?.id ?? null;
}

export const appStore = createStore<AppState>({
  tab: 'stations',
  lang: readStoredLang(),
  theme: readInitialTheme(),
  stations: [],
  settings: { ...DEFAULT_SETTINGS },
  query: '',
  libraryReady: false,
  libraryFailure: null,
  stationId: null,
  playback: 'idle',
  attempt: 0,
  route: null,
  error: null,
  track: null,
  artworkUrl: null,
  search: { ...EMPTY_SEARCH },
  history: [],
  stats: [],
  sleep: null,
  player: 'bar',
});
